import DropdownSheet from 'components/Luxkit/Dropdown/Sheet/DropdownSheet'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import React, { ComponentProps, useCallback, useContext, useEffect, useRef, useState } from 'react'
import OfferDatePicker from './OfferDatePicker'
import OfferDatePickerSelectBox from './OfferDatePickerSelectBox'
import DropdownSheetBase from 'components/Luxkit/Dropdown/Sheet/DropdownSheetBase'
import DropdownSheetBody from 'components/Luxkit/Dropdown/Sheet/DropdownSheetBody'
import DropdownSheetFooter from 'components/Luxkit/Dropdown/Sheet/DropdownSheetFooter'
import DropdownSheetHeader from 'components/Luxkit/Dropdown/Sheet/DropdownSheetHeader'
import Sheet from 'components/utils/Sheet'
import BodyText from 'components/Luxkit/Typography/BodyText'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import LineArrowRightIcon from 'components/Luxkit/Icons/line/LineArrowRightIcon'
import Group from 'components/utils/Group'
import { AccommodationMobileDayCellSelectHandler } from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendarSmallDayCell'
import moment from 'moment'

interface Props extends Pick<ComponentProps<typeof DropdownSheet>, 'open' | 'boundaryRef'> {
  checkInDate?: string;
  checkOutDate?: string;
}

function OfferDatePickerSelect(props: Props) {
  const { open, boundaryRef, checkInDate, checkOutDate } = props
  const [dates, setDates] = useState<{ checkIn?: string, checkOut?: string }>({
    checkIn: checkInDate,
    checkOut: checkOutDate,
  })

  useEffect(() => {
    setDates({ checkIn: checkInDate, checkOut: checkOutDate })
  }, [checkInDate, checkOutDate])

  const hasDates = !!(dates.checkIn && dates.checkOut)
  const dispatch = useOfferPageDispatch()
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  // const [selectedMonth, setSelectedMonth] = useState<string | undefined>(checkInDate)

  const onCloseCross = useCallback(() => {
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CloseFilter, CALENDAR_GA_EVENT_LABEL.CloseCross)
    dispatch({ type: OfferPageActions.setDates, checkIn: dates.checkIn, checkOut: dates.checkOut })
    dispatch({ type: OfferPageActions.closeFilter })
  }, [dates.checkIn, dates.checkOut, dispatch, onUserAnalyticsAction])

  const onApply = useCallback(() => {
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.Apply)
    dispatch({ type: OfferPageActions.setDates, checkIn: dates.checkIn, checkOut: dates.checkOut })
    dispatch({ type: OfferPageActions.closeFilter })
  }, [dates.checkIn, dates.checkOut, dispatch, onUserAnalyticsAction])

  const onReset = useCallback(() => {
    setDates({ checkIn: undefined, checkOut: undefined })
  }, [])

  const triggerRef = useRef<HTMLButtonElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)

  // temporarily disabled until we have updated designs for the month view when empty
  // const onShowMonth = useCallback(() => setSelectedMonth(undefined), [])
  // const onSelectMonth = useCallback<AccommodationCalendarYearSelectHandler>((month) => {
  //   setSelectedMonth(formatIsoDate(new Date(month.year, month.month, 1)))
  // }, [])

  const onDatesChange = useCallback<AccommodationMobileDayCellSelectHandler>(day => {
    if (dates.checkIn && !dates.checkOut && moment(day.date).isAfter(dates.checkIn)) {
      setDates({ checkIn: dates.checkIn, checkOut: day.date })
    } else {
      setDates({ checkIn: day.date, checkOut: undefined })
    }
  }, [dates.checkIn, dates.checkOut])

  return <div>
    <OfferDatePickerSelectBox ref={triggerRef} />
    <DropdownSheetBase
      size="XL"
      height="max"
      anchorRef={triggerRef}
      triggerRef={triggerRef}
      boundaryRef={boundaryRef}
      onClose={onCloseCross}
      open={open}
      data-testid="offer-checkin-select"
    >
      <DropdownSheetHeader
        title="Select check-in date"
        size="M"
        alignment="start"
        onDismissClick={onCloseCross}
        // onBackClick={selectedMonth ? onShowMonth : undefined}
      />
      <DropdownSheetBody ref={bodyRef}>
        <Sheet padding="24 0">
          <OfferDatePicker
            scrollerRef={bodyRef}
            size="large"
            // onBackToMonth={onShowMonth}
            // onMonthSelect={onSelectMonth}
            // selectedMonth={selectedMonth}
            variant="day-only"
            checkInDate={dates.checkIn}
            checkOutDate={dates.checkOut}
            onDatesChange={onDatesChange}
          />
        </Sheet>
      </DropdownSheetBody>
      <DropdownSheetFooter
        startSlot={<>
          {dates.checkIn && dates.checkOut && <BodyText variant="medium">
            <Group direction="horizontal" verticalAlign="center">
              <span><FormatDateTime value={dates.checkIn} format="short-day-name-day-month" /></span>
              <LineArrowRightIcon size="XS" />
              <span><FormatDateTime value={dates.checkOut} format="short-day-name-day-month" /></span>
            </Group>
          </BodyText>}
        </>}
        secondaryActionProps={hasDates ? { children: 'Reset dates', onClick: onReset, kind: 'tertiary' } : undefined }
        primaryActionProps={{
          'data-testid': 'OfferDatePickerSelect-apply',
          children: 'Apply',
          onClick: onApply,
        }}
      />
    </DropdownSheetBase>
  </div>
}

type MappedProps = Pick<Props, 'open' | 'checkInDate' | 'checkOutDate'>

export default withOfferPageState((state): MappedProps => ({
  open: state.activeFilter === 'checkin',
  checkInDate: state.checkInDate,
  checkOutDate: state.checkOutDate,
}), OfferDatePickerSelect)
